"use client";

import { CircleCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const healthBenefits = [{
  title: "Protects Heart Health",
  description: "Significantly reduces the risk of high blood pressure, heart attacks, and strokes associated with nighttime oxygen deprivation."
}, {
  title: "Restores Daytime Energy",
  description: "Eliminates chronic fatigue and daytime sleepiness, allowing you to wake up feeling refreshed and stay focused."
}, {
  title: "Improves Mental Clarity",
  description: "Enhances memory, concentration, and overall mood by ensuring your brain receives the restorative, uninterrupted sleep it needs."
}, {
  title: "Lowers Diabetes Risk",
  description: "Helps regulate blood sugar levels and improves insulin sensitivity, which can be negatively impacted by untreated sleep apnea."
}];
export default function WhyTreatmentMatters() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="why-treatment-matters" className="bg-secondary py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="relative order-2 lg:order-1">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent rounded-2xl transform -translate-x-4 translate-y-4 -z-10"></div>
            <img src="https://solutionsforsleepapnea.com/wp-content/uploads/2026/04/image-2.webp" alt="Detailed medical illustration showing the airway and the effects of sleep apnea" className="w-full h-auto rounded-2xl shadow-xl border border-border/50 object-cover aspect-[4/3] lg:aspect-auto" />
          </div>

          {}
          <div className="order-1 lg:order-2 flex flex-col justify-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 tracking-tight">
              Why Treating Sleep Apnea Matters
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Left untreated, sleep apnea is more than just disruptive snoring—it is a serious medical condition that impacts your entire body. Proper treatment can dramatically improve your quality of life and protect your long-term health.
            </p>

            <div className="space-y-6 mb-10">
              {healthBenefits.map((benefit, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                  <div className="mt-1 bg-background rounded-full p-0.5 shadow-xs">
                    <CircleCheck className="size-6 text-chart-2 shrink-0" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div>
              <Button asChild size="lg" className="w-full sm:w-auto group">
                <Link to="/TreatmentsPage">
                  Explore Treatment Options
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}